import { useMergingState, useToggle } from '@/hooks';
import {
	PublicWorkHistoryLayerActions,
	PublicWorkHistoryLayerSelectors,
	useAppDispatch,
} from '@/stores';
import {
	MouseEventHandler,
	SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { ResizeCallbackData } from 'react-resizable';

import { getColumns, HEADER_HEIGHT, TH_HEIGHT, TR_HEIGHT } from './config';
import { PublicWorkHistoryViewProps } from './type';

export const useLogic = (props: PublicWorkHistoryViewProps) => {
	const [openModal, onToggle] = useToggle(false);
	const dispatch = useAppDispatch();

	const dataSource = useSelector(PublicWorkHistoryLayerSelectors.selectList);
	const loading = useSelector(PublicWorkHistoryLayerSelectors.selectFetching);

	const [state, setSate] = useMergingState({
		visible: false,
		loading: false,
		width: 600,
		height: 260,
	});

	const handleClosePopover = useCallback(() => {
		setSate({
			visible: false,
		});
	}, []);

	useEffect(() => {
		setSate({ ...state, loading: loading });
	}, [loading]);

	const handleOpenPopover = useCallback(
		async (open: boolean) => {
			if (open) {
				setSate({
					visible: open,
					loading: true,
				});

				dispatch(
					PublicWorkHistoryLayerActions.getPublicWorkHistoryLayerListRequest({
						franchiseId: Number(props?.id),
					})
				);
			} else {
				setSate({
					visible: open,
					loading: false,
				});
			}
		},
		[props?.id]
	);

	const onClick: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
		e.stopPropagation();
	}, []);

	const scrollHeight = useMemo(() => {
		if (dataSource.length <= 3) return undefined;
		if (
			dataSource.length * TR_HEIGHT <
			state.height - HEADER_HEIGHT - TH_HEIGHT
		)
			return undefined;

		return state.height - HEADER_HEIGHT - TH_HEIGHT;
	}, [dataSource, state.height]);

	const handleResize = useCallback(
		(e?: SyntheticEvent<Element, Event>, data?: ResizeCallbackData) => {
			if (!data?.size) return;
			setSate({
				width: data?.size?.width,
				height: data?.size?.height,
			});
		},
		[]
	);

	return {
		dataSource,
		columns: getColumns(),
		loading,
		openModal,
		onToggle,
		handleClosePopover,
		state,
		onClick,
		scrollHeight,
		handleOpenPopover,
		handleResize,
	};
};
